import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Play, Pause } from "lucide-react";

const AUDIO_SRC = "/feels-right.mp3";
const COVER_SRC = "/feels-right-cover.webp";
const BASE_URL = "https://alekzsoto.com";
const RELEASE = "09 · 25 · MMXXVI";
const ACCENT = "#ff1a2e"; // one small nod to the cover

export default function FeelsRight() {
  const audioRef = useRef(null);
  const fadeRafRef = useRef(0);

  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [inverted, setInverted] = useState(false);

  const fadeVolume = (from, to, ms) => {
    const a = audioRef.current;
    if (!a) return;
    cancelAnimationFrame(fadeRafRef.current);
    const start = performance.now();
    a.volume = from;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease in-out
      a.volume = from + (to - from) * eased;
      if (t < 1) fadeRafRef.current = requestAnimationFrame(tick);
    };
    fadeRafRef.current = requestAnimationFrame(tick);
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      if (a.currentTime >= (a.duration || Infinity) - 0.05) {
        a.currentTime = 0;
      }
      a.volume = 0;
      await a.play();
      setPlaying(true);
      fadeVolume(0, 1, 1200);
    } else {
      fadeVolume(a.volume, 0, 350);
      setTimeout(() => {
        a.pause();
        setPlaying(false);
      }, 360);
    }
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => {
      setDuration(a.duration || 0);
    };
    const onTime = () => {
      const dur = a.duration || 0;
      setCurrent(a.currentTime);
      setProgress(dur ? a.currentTime / dur : 0);
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const fmt = (t) => {
    if (!isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = p * a.duration;
  };

  return (
    <div
      className="feelsright-root relative min-h-screen w-full overflow-hidden bg-black text-white"
      style={{ "--accent": ACCENT }}
      data-testid="feels-right-page"
    >
      <Helmet>
        <title>FEELS RIGHT — ALEKZ (Alex Soto) | Private Pre-Release Listen</title>
        <meta
          name="description"
          content="FEELS RIGHT — private pre-release listen. The new single from ALEKZ (Alex Soto). Out September 25, 2026."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`${BASE_URL}/feels-right`} />
        <meta property="og:type" content="music.song" />
        <meta property="og:title" content="FEELS RIGHT — ALEKZ (Private Pre-Release Listen)" />
        <meta property="og:description" content="Private pre-release listen. Out September 25, 2026." />
        <meta property="og:url" content={`${BASE_URL}/feels-right`} />
        <meta property="og:image" content={`${BASE_URL}${COVER_SRC}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FEELS RIGHT — ALEKZ" />
        <meta name="twitter:image" content={`${BASE_URL}${COVER_SRC}`} />
        <meta name="theme-color" content="#000000" />
      </Helmet>

      <audio ref={audioRef} src={AUDIO_SRC} preload="metadata" />

      {/* Minimal grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[1] opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Top bar */}
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 lg:px-14 pt-8">
        <a
          href="/"
          className="font-mono-wide text-[10px] tracking-[0.45em] text-white/70 hover:text-white transition-colors"
          data-testid="fr-brand"
        >
          ALEKZ
        </a>
        <a
          href="/"
          data-testid="fr-back"
          className="font-mono-wide text-[10px] tracking-[0.45em] text-white/50 hover:text-white transition-colors"
        >
          RETURN
        </a>
      </header>

      {/* Hero — single, quiet composition */}
      <main className="relative z-20 min-h-[calc(100vh-80px)] flex items-center justify-center px-6 sm:px-10 lg:px-14 py-12">
        <div className="w-full max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center">

            {/* Cover — clean, no overlay */}
            <div className="fr-cover relative aspect-square w-full max-w-[440px] mx-auto md:mx-0 md:ml-auto">
              <button
                type="button"
                onClick={() => setInverted((v) => !v)}
                aria-label="Toggle cover invert"
                data-testid="fr-cover-toggle"
                className="block w-full h-full cursor-pointer"
              >
                <img
                  src={COVER_SRC}
                  alt="FEELS RIGHT — ALEKZ single cover"
                  draggable={false}
                  data-testid="fr-cover"
                  className="w-full h-full object-cover transition-[filter] duration-500 ease-out"
                  style={{ filter: inverted ? "invert(1)" : "invert(0)" }}
                />
              </button>
              {/* Hairline frame */}
              <span aria-hidden className="absolute inset-0 border border-white/10 pointer-events-none" />
            </div>

            {/* Type stack */}
            <div className="text-left">
              <div className="flex items-center gap-3 mb-8">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: ACCENT }}
                />
                <p
                  className="font-mono-wide text-[10px] tracking-[0.5em] text-white/70"
                  data-testid="fr-tag"
                >
                  NEW SINGLE
                </p>
              </div>

              <h1
                className="font-display text-white leading-[0.86]"
                style={{
                  fontSize: "clamp(3rem, 8.5vw, 7.5rem)",
                  letterSpacing: "-0.005em",
                }}
                data-testid="fr-title"
              >
                <span className="block">Feels</span>
                <span className="block">Right</span>
              </h1>

              <div className="mt-10 space-y-1.5 font-mono-wide text-[10px] tracking-[0.4em] text-white/55">
                <p>
                  <span className="text-white/40 mr-3">OUT</span>
                  <span className="text-white/85">{RELEASE}</span>
                </p>
                <p>
                  <span className="text-white/40 mr-3">BY</span>
                  <span className="text-white/85">ALEKZ</span>
                </p>
              </div>
            </div>
          </div>

          {/* Slim inline preview player */}
          <div className="mt-16 md:mt-24 max-w-4xl mx-auto">
            <div
              className="flex items-center gap-5 px-5 py-4 border border-white/12 rounded-full bg-black/40"
              data-testid="fr-player"
            >
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
                data-testid="fr-play-btn"
                className="shrink-0 w-11 h-11 rounded-full inline-flex items-center justify-center border border-white/30 hover:border-white transition-colors"
              >
                {playing ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4 translate-x-[1px]" />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between font-mono-wide text-[9px] tracking-[0.45em] text-white/55 mb-2">
                  <span>PRIVATE PRE-RELEASE LISTEN</span>
                  <span>
                    <span data-testid="fr-current-time">{fmt(current)}</span>
                    <span className="mx-2 text-white/25">/</span>
                    <span data-testid="fr-total-time">{fmt(duration)}</span>
                  </span>
                </div>
                <div
                  className="relative h-px bg-white/12 cursor-pointer"
                  onClick={onSeek}
                  data-testid="fr-progress"
                >
                  <div
                    className="absolute inset-y-0 left-0 transition-[width] duration-100"
                    style={{
                      width: `${progress * 100}%`,
                      background: "#ffffff",
                    }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full transition-[left] duration-100"
                    style={{
                      left: `calc(${progress * 100}% - 4px)`,
                      background: ACCENT,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .fr-cover { transition: transform 500ms cubic-bezier(0.2,0.8,0.2,1); }
        .fr-cover:hover { transform: translateY(-3px); }
        .feelsright-root ::selection { background: ${ACCENT}; color: #fff; }
      `}</style>
    </div>
  );
}
